/**
 * AskModal — "Ask <desk>" from a thread's Do next (kanban d3ec3001).
 *
 * Opens with the request Merlin wrote when it filed the brief (the PROPOSED
 * rows for the step), the people the desk names as chips, and a picker for
 * the account's roles, the org chart's teams and the staff directory. Send
 * is the only way anything leaves (owner ruling bz2bb6); the wording and
 * the recipients are the sender's to change first.
 */
import { useEffect, useMemo, useRef, useState } from 'react';

import { trackEvent } from '../../services/portalTracking';
import {
  discardDataRequest,
  getInvestigationAskTargets,
  getThreadAskTargets,
  sendInvestigationAsk,
  sendThreadAsk,
  type AskKind,
  type DoorAskTargetsRead,
  type DoorDataRequestRead,
  type DoorThreadAskSendRead,
} from '../../services/qcpDataRequests';
import {
  customerCarrier,
  defaultRecipients,
  expandRecipients,
  fallbackBody,
  fallbackTitle,
  deliveryLine,
  fanoutLine,
  filterTargets,
  personToken,
  roleToken,
  sendBlocker,
  soleMatch,
  targetCount,
  teamLabel,
  teamToken,
  toggleToken,
  tokenKey,
  tokenString,
  type AskRecipientToken,
} from './askTargetsView';
import { matchCountLabel } from '../../pages/quality-triage/routePickView';
import styles from './AskModal.module.css';

/** How long the typing rests before the filter is recorded once. */
const FILTER_TRACK_MS = 500;

/**
 * Where the ask is sent from: a thread's Do next desk, or a case file's
 * outstanding item (kanban 52a4147a). The case form carries the row, so the
 * ask comes back pinned to it.
 */
export type AskScope =
  /** `label`: the number the item goes by on screen (proposal 6: its case's
   *  INV once it has one); `no` stays the thread's own number for the API. */
  | { kind: 'thread'; id: string; no: string; label?: string }
  | { kind: 'case'; id: string; no: string; task: { id: string; label: string } | null };

export interface AskModalProps {
  scope: AskScope;
  desk: string;
  stepNo: number | null;
  /** The desk's open steps, for the message when Merlin wrote none. */
  steps: string[];
  /** Merlin's unsent drafts for the step (may be empty on an older filing). */
  drafts: DoorDataRequestRead[];
  viewerName: string | null;
  onClose: () => void;
  onSent: (result: DoorThreadAskSendRead) => void;
  onDiscarded: (discarded: DoorDataRequestRead[]) => void;
  /** Wording to start from when there is no draft: "Ask again" on an
   *  answered ask reopens its question (proposal 1). */
  prefill?: { title: string; body: string } | null;
  /** Merlin's chat drafts in this scope (no Do next step). Their ids go
   *  with the send, so a draft to a person kept on it goes out as this ask
   *  instead of staying "not sent" beside it (kanban 31f2809d). */
  chatDrafts?: DoorDataRequestRead[];
}

const first = (name: string) => name.split(' ')[0] || name;

export function AskModal({
  scope,
  desk,
  stepNo,
  steps,
  drafts,
  viewerName,
  onClose,
  onSent,
  onDiscarded,
  prefill = null,
  chatDrafts = [],
}: AskModalProps) {
  const [targets, setTargets] = useState<DoorAskTargetsRead | null>(null);
  const [targetsError, setTargetsError] = useState(false);
  const [tokens, setTokens] = useState<AskRecipientToken[]>(() =>
    defaultRecipients(desk, drafts, null, viewerName),
  );
  const [title, setTitle] = useState(drafts[0]?.question_title ?? prefill?.title ?? fallbackTitle(steps));
  const shownNo = scope.kind === 'thread' ? (scope.label ?? scope.no) : scope.no;
  const [body, setBody] = useState(
    drafts[0]?.question_body ?? prefill?.body ?? fallbackBody(shownNo, steps),
  );
  // What every tracked action on this modal says about where it was opened.
  const where =
    scope.kind === 'thread'
      ? { scope: 'thread', thread_no: scope.no, task_id: null }
      : { scope: 'case', investigation_no: scope.no, task_id: scope.task?.id ?? null };
  const [pickerOpen, setPickerOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    trackEvent('action', 'qcp_ask_modal_open', {
      ...where,
      desk,
      step_no: stepNo,
      drafts: drafts.length,
    });
    let alive = true;
    (scope.kind === 'thread' ? getThreadAskTargets(scope.id) : getInvestigationAskTargets(scope.id))
      .then((t) => {
        if (!alive) return;
        setTargets(t);
        // Re-resolve the desk now the directory is here (names → people,
        // "customer" → the rep it relays through).
        setTokens(defaultRecipients(desk, drafts, t, viewerName));
      })
      .catch(() => {
        if (alive) setTargetsError(true);
      });
    return () => {
      alive = false;
    };
    // The modal is keyed by desk by its host; these are fixed for its life.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const people = useMemo(() => expandRecipients(tokens), [tokens]);
  const blocker = sendBlocker(tokens);
  const filtered = useMemo(() => (targets ? filterTargets(targets, query) : null), [targets, query]);
  const has = (t: AskRecipientToken) => tokens.some((o) => tokenKey(o) === tokenKey(t));
  const typed = query.trim();
  const matchCount = filtered ? targetCount(filtered) : 0;
  // Exactly one match picks it (the route picker's rule, E2E pass 2); once
  // per match, so removing that chip while the text stays does not re-add it.
  const sole = filtered ? soleMatch(filtered, query) : null;
  const soleKey = sole ? tokenKey(sole) : null;
  const autoPicked = useRef<string | null>(null);
  useEffect(() => {
    if (!sole || !soleKey) {
      autoPicked.current = null;
      return;
    }
    if (autoPicked.current === soleKey) return;
    autoPicked.current = soleKey;
    setTokens((cur) => (cur.some((o) => tokenKey(o) === soleKey) ? cur : [...cur, sole]));
    trackEvent('action', 'qcp_ask_picker_sole_match', { ...where, query: typed, picked: soleKey });
    // `sole` is rebuilt every render; its key is what changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [soleKey]);
  useEffect(() => {
    if (!typed || !filtered) return;
    const t = window.setTimeout(() => {
      trackEvent('action', 'qcp_ask_picker_filter', { ...where, query: typed, matches: matchCount });
    }, FILTER_TRACK_MS);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typed, matchCount, !!filtered]);
  const pick = (t: AskRecipientToken) => {
    trackEvent('action', 'qcp_ask_picker_pick', {
      ...where,
      query: typed,
      picked: tokenKey(t),
      result: has(t) ? 'removed' : 'added',
    });
    setTokens((cur) => toggleToken(cur, t));
  };

  // One click either way (kanban 196e9d9b): Send asks a question and waits
  // for the answer; Pass along hands the piece to the desk that owns it and
  // waits for nothing — the quality issue stays here.
  const send = async (kind: AskKind = 'ask') => {
    if (busy || blocker || !title.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const payload = {
        step_no: stepNo,
        desk,
        recipients: tokens.map(tokenString),
        question_title: title.trim(),
        question_body: body,
        draft_ids: [...new Set([...drafts, ...chatDrafts].map((d) => d.id))],
        kind,
      };
      const result =
        scope.kind === 'thread'
          ? await sendThreadAsk(scope.id, payload)
          : await sendInvestigationAsk(scope.id, { ...payload, task_id: scope.task?.id ?? null });
      trackEvent('action', kind === 'pass_along' ? 'qcp_pass_along_send' : 'qcp_ask_send', {
        ...where,
        // The Send button's own data-action row covers the recorder; this
        // one lands after the request, past the bridge's dedupe window.
        recorded_by_dom: true,
        desk,
        step_no: stepNo,
        recipients: result.sent.length,
        held_for_check: result.sent.filter((r) => r.handoff?.state === 'pending').length,
        reworded: drafts.length > 0 && (title.trim() !== drafts[0].question_title || body !== drafts[0].question_body),
      });
      onSent(result);
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'That did not go through. Try again in a moment.');
      setBusy(false);
    }
  };

  const discard = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const done: DoorDataRequestRead[] = [];
      for (const d of drafts) done.push(await discardDataRequest(d.id, 'discarded by Jess'));
      trackEvent('action', 'qcp_ask_discard', { ...where, desk, step_no: stepNo });
      onDiscarded(done);
    } catch {
      setError('That did not go through. Try again in a moment.');
      setBusy(false);
    }
  };

  const whom = people.length === 1 ? first(people[0].person.name) : people.length > 1 ? `${people.length} people` : '';
  const sendLabel = whom ? `Ask ${whom}` : 'Ask';
  const passLabel = whom ? `Pass along to ${whom}` : 'Pass along';

  return (
    <div className={styles.back} onClick={onClose} data-testid="ask-modal">
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ask-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.head}>
          <div>
            <div className={styles.eyebrow}>
              Ask · {shownNo}
              {stepNo != null && ` · step ${stepNo}`}
              {scope.kind === 'case' && scope.task && ' · outstanding item'}
            </div>
            <h2 id="ask-modal-title" className={styles.title}>
              Ask {desk || 'someone'}
            </h2>
          </div>
          <button
            type="button"
            className={styles.x}
            onClick={onClose}
            aria-label="Close"
            data-action="qcp_ask_close"
            data-outcome="close"
          >
            ×
          </button>
        </div>

        <div className={styles.field}>
          <div className={styles.label}>To</div>
          <div className={styles.chips} data-testid="ask-chips">
            {tokens.map((t) => (
              <span key={tokenKey(t)} className={styles.chip} data-kind={t.kind} data-unresolved={t.kind === 'person' && t.unresolved ? 'true' : undefined}>
                {t.kind === 'person' && (
                  <>
                    <b>{t.name}</b>
                    <span className={styles.meta}>{t.unresolved ? 'not in the staff directory' : t.email ?? ''}</span>
                  </>
                )}
                {t.kind === 'role' && (
                  <>
                    <b>{t.label}</b>
                    <span className={styles.meta}>{t.person ? `→ ${t.person.name}` : 'nobody on file'}</span>
                  </>
                )}
                {t.kind === 'team' && (
                  <>
                    <b>{teamLabel(t.label)}</b>
                    <span className={styles.meta}>→ {t.members.map((m) => m.name).join(', ')}</span>
                  </>
                )}
                {t.kind === 'customer' && (
                  <>
                    <b>The customer</b>
                    <span className={styles.meta}>
                      {customerCarrier(tokens)
                        ? `via ${first(customerCarrier(tokens)!.name)}, who relays`
                        : 'no sales rep on file to relay'}
                    </span>
                  </>
                )}
                <button
                  type="button"
                  className={styles.rm}
                  onClick={() => setTokens((cur) => toggleToken(cur, t))}
                  aria-label={`Remove ${t.kind === 'person' ? t.name : t.kind === 'customer' ? 'the customer' : t.label}`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
          <div className={styles.pickerWrap}>
            <button
              type="button"
              className={styles.ghostSmall}
              onClick={() => setPickerOpen((v) => !v)}
              aria-expanded={pickerOpen}
            >
              + Add people or a team
            </button>
            {pickerOpen && (
              <div className={styles.picker} data-testid="ask-picker">
                <input
                  type="search"
                  className={styles.search}
                  placeholder="Type a name, a role, or a team…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  autoFocus
                />
                {typed && filtered && (
                  <p className={styles.matchLine} data-testid="ask-match-count" aria-live="polite">
                    {matchCountLabel(matchCount, typed)}
                    {matchCount > 1 && ' · click who you mean'}
                  </p>
                )}
                <div className={styles.pickerBody}>
                  {!filtered && !targetsError && <p className={styles.note}>Loading who can be asked…</p>}
                  {targetsError && (
                    <p className={styles.note}>Could not load the directory. Type a full name and Send will check it.</p>
                  )}
                  {filtered && filtered.roles.length > 0 && (
                    <>
                      <h4 className={styles.section}>Roles for this account</h4>
                      {filtered.roles.map((r) => {
                        const t = roleToken(r);
                        return (
                          <button
                            key={r.token}
                            type="button"
                            className={`${styles.opt} ${has(t) ? styles.optPicked : ''}`}
                            aria-pressed={has(t)}
                            disabled={!r.available}
                            onClick={() => pick(t)}
                          >
                            <span>
                              {r.label}
                              <span className={styles.sub}>{r.available ? r.detail ?? '' : r.reason ?? 'nobody on file'}</span>
                            </span>
                            <span className={styles.arrow}>{r.resolved_name ? `→ ${r.resolved_name}` : ''}</span>
                          </button>
                        );
                      })}
                    </>
                  )}
                  {filtered && filtered.teams.length > 0 && (
                    <>
                      <h4 className={styles.section}>Teams</h4>
                      {filtered.teams.map((tm) => {
                        const t = teamToken(tm);
                        return (
                          <button
                            key={tm.key}
                            type="button"
                            className={`${styles.opt} ${has(t) ? styles.optPicked : ''}`}
                            aria-pressed={has(t)}
                            disabled={tm.members.length === 0}
                            onClick={() => pick(t)}
                          >
                            <span>
                              {tm.label}
                              <span className={styles.sub}>
                                {tm.source}
                                {tm.unresolved.length > 0 &&
                                  ` · not reachable: ${tm.unresolved.map((u) => `${u.name} (${u.reason})`).join('; ')}`}
                              </span>
                            </span>
                            <span className={styles.arrow}>
                              {tm.members.length > 0 ? `→ ${tm.members.map((m) => first(m.name)).join(', ')}` : 'nobody reachable'}
                            </span>
                          </button>
                        );
                      })}
                    </>
                  )}
                  {filtered && filtered.staff.length > 0 && (
                    <>
                      <h4 className={styles.section}>People</h4>
                      {filtered.staff.map((p) => {
                        const t = personToken(p);
                        return (
                          <button
                            /* The directory sends "" (not null) for a person with no
                               email, so keying on the email alone gave every such
                               person the same key and React left stale rows behind
                               when the filter narrowed (staging E2E pass 3). */
                            key={p.email ? `e:${p.email}` : `n:${p.name}`}
                            type="button"
                            className={`${styles.opt} ${has(t) ? styles.optPicked : ''}`}
                            aria-pressed={has(t)}
                            disabled={!p.email}
                            data-action="qcp_ask_person_pick"
                            onClick={() => pick(t)}
                          >
                            <span>{p.name}</span>
                            <span className={styles.arrow}>{p.email || 'no email on file'}</span>
                          </button>
                        );
                      })}
                    </>
                  )}
                  {filtered && matchCount === 0 && (
                    <p className={styles.note}>Teams come from the org chart; ask an admin to add one.</p>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className={`${styles.hint} ${blocker ? styles.hintWarn : ''}`} data-testid="ask-fanout">
            {blocker ?? fanoutLine(tokens)}
          </div>
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="ask-title">
            What you are asking
          </label>
          <input id="ask-title" className={styles.input} value={title} onChange={(e) => setTitle(e.target.value)} disabled={busy} />
        </div>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="ask-body">
            The message they get
            <span className={styles.labelNote}>
              {drafts.length > 0
                ? ' — Merlin wrote it when it filed the brief; change anything'
                : prefill
                  ? ' — the question you asked before; change anything'
                  : ' — written from the item; change anything'}
            </span>
          </label>
          <textarea id="ask-body" className={styles.textarea} rows={9} value={body} onChange={(e) => setBody(e.target.value)} disabled={busy} />
        </div>

        <div className={styles.channels}>
          <b>How they get it</b> — {deliveryLine(tokens)}
        </div>
        <div className={styles.channels} data-testid="ask-kinds">
          <b>Ask</b> waits for their answer and reminds them. <b>Pass along</b> makes the piece theirs: sent once,
          no reminders, nothing waiting on you here; the quality issue stays yours. Merlin checks first whether the
          desk already has it and says so on the item.
        </div>

        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.send}
            onClick={() => void send('ask')}
            disabled={busy || !!blocker || !title.trim()}
            data-action="qcp_ask_send"
            data-outcome="request"
            {...(scope.kind === 'thread' ? { 'data-target-thread-id': scope.id } : { 'data-target-case-id': scope.id })}
          >
            {sendLabel}
          </button>
          <button
            type="button"
            className={`${styles.send} ${styles.pass}`}
            onClick={() => void send('pass_along')}
            disabled={busy || !!blocker || !title.trim()}
            data-action="qcp_pass_along_send"
            data-outcome="request"
            data-testid="ask-pass-along"
            {...(scope.kind === 'thread' ? { 'data-target-thread-id': scope.id } : { 'data-target-case-id': scope.id })}
          >
            {passLabel}
          </button>
          <button type="button" className={styles.ghost} onClick={onClose} disabled={busy}>
            Not now
          </button>
          {!title.trim() && !blocker && (
            <span className={styles.hint} data-testid="ask-needs-title">
              Say what you are asking (the box above) to send it.
            </span>
          )}
          {drafts.length > 0 && (
            <button type="button" className={`${styles.ghost} ${styles.danger}`} onClick={() => void discard()} disabled={busy}>
              Don&apos;t ask
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default AskModal;
